import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CoachingAttemptStatus,
  CoachingAttemptView,
  CoachingCaseKind,
} from '@euler/model/coaching';
import dayjs from 'dayjs';
import { FileText, Play, RotateCcw } from 'lucide-react-native';
import { memo } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { Chip } from '../../components/Chip';
import { formatName, levelColor, levelName } from '../../copy';

/** Still in conversation: it can be continued. */
function inPlay(attempt: CoachingAttemptView) {
  return (
    attempt.status === CoachingAttemptStatus.Ready ||
    attempt.status === CoachingAttemptStatus.Active
  );
}

/**
 * One started practice with its result: continue it, read its report, or
 * play it again.
 */
export const AttemptRow = memo(
  ({
    attempt,
    replaying,
    onReplay,
    onOpen,
  }: {
    attempt: CoachingAttemptView;
    replaying: boolean;
    onReplay: () => void;
    /** Into the scene while in play, else to the report. */
    onOpen: () => void;
  }) => {
    const playing = inPlay(attempt);
    const reported = attempt.status !== CoachingAttemptStatus.Abandoned;
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          padding: rpx(14),
          gap: rpx(8),
        }}
      >
        <View style={{ flexDirection: 'row', gap: rpx(6) }}>
          <Chip
            text={levelName(attempt.difficulty)}
            color={levelColor(attempt.difficulty)}
          />
          <Chip text={formatName(attempt.format)} color={aiColors.primary} />
          {attempt.caseKind === CoachingCaseKind.Story ? (
            <Chip text="我的场景" color={aiColors.amber} />
          ) : null}
        </View>
        <Typography.Text size={rpx(14)} bold color={aiColors.foreground}>
          {attempt.title}
        </Typography.Text>
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          {`${attempt.variationLabel} · ${dayjs(attempt.createdAt).format(
            'M月D日 HH:mm',
          )}`}
        </Typography.Text>
        <View style={{ flexDirection: 'row', gap: rpx(18) }}>
          {playing || reported ? (
            <Pressable
              onPress={onOpen}
              accessibilityRole="button"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(6),
              }}
            >
              {playing ? (
                <Play size={rpx(14)} color={aiColors.primary} />
              ) : (
                <FileText size={rpx(14)} color={aiColors.primary} />
              )}
              <Typography.Text size={rpx(13)} color={aiColors.primary}>
                {/* Never opened, it starts: only a practice under way goes on. */}
                {attempt.status === CoachingAttemptStatus.Ready
                  ? '开始'
                  : playing
                  ? '继续'
                  : '看报告'}
              </Typography.Text>
            </Pressable>
          ) : null}
          <Pressable
            onPress={onReplay}
            disabled={replaying}
            accessibilityRole="button"
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(6),
              alignSelf: 'flex-start',
            }}
          >
            {replaying ? (
              <ActivityIndicator size="small" color={aiColors.primary} />
            ) : (
              <RotateCcw size={rpx(14)} color={aiColors.primary} />
            )}
            <Typography.Text size={rpx(13)} color={aiColors.primary}>
              再练一次
            </Typography.Text>
          </Pressable>
        </View>
      </View>
    );
  },
);
